import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Tooltip,
  Paper,
  Typography,
  Fade
} from '@mui/material';
import {
  People as PeopleIcon,
  FiberManualRecord as DotIcon
} from '@mui/icons-material';
import ActiveUsersOverlay from './ActiveUsersOverlay';
import io from 'socket.io-client';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Get overall status color based on user states
 */
const getOverallStatusColor = (stats) => {
  if (stats.away > 0) return '#F44336'; // Red if any away
  if (stats.idle > 0) return '#FFC107'; // Yellow if any idle
  return '#4CAF50'; // Green if all active
};

const ActiveUsersWidget = () => {
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState({ total: 0, active: 0, idle: 0, away: 0 });
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);
  const statsRef = useRef({ total: 0, active: 0, idle: 0, away: 0 });

  // Fetch initial count
  const fetchCount = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/active-users/count`);
      const data = await response.json();
      
      if (data.success) {
        // Fetch full stats
        const fullResponse = await fetch(`${API_BASE}/api/active-users`);
        const fullData = await fullResponse.json();
        
        if (fullData.success) {
          const newStats = fullData.stats || { total: 0, active: 0, idle: 0, away: 0 };
          setStats(newStats);
          statsRef.current = newStats;
        } else {
          const newStats = { total: data.count || 0, active: 0, idle: 0, away: 0 };
          setStats(newStats);
          statsRef.current = newStats;
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching active user count:', error);
      setLoading(false);
    }
  };

  // Setup Socket.IO connection
  useEffect(() => {
    fetchCount();

    const socket = io(API_BASE, {
      transports: ['websocket'],
      reconnection: true
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('admin:join-active-users');
    });

    socket.on('active-user:update', (data) => {
      const newStats = data.stats || { total: 0, active: 0, idle: 0, away: 0 };
      
      // Only update if stats actually changed
      const statsChanged = 
        statsRef.current.total !== newStats.total ||
        statsRef.current.active !== newStats.active ||
        statsRef.current.idle !== newStats.idle ||
        statsRef.current.away !== newStats.away;
      
      if (statsChanged) {
        setStats(newStats);
        statsRef.current = newStats;
      }
      setLoading(false);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('admin:leave-active-users');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  // Memoize status color to prevent recalculation on every render
  const statusColor = useMemo(() => getOverallStatusColor(stats), [stats.total, stats.active, stats.idle, stats.away]);

  return (
    <>
      <Tooltip title="View Active Users" arrow>
        <Paper
          elevation={4}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1300,
            borderRadius: '50%',
            backgroundColor: '#1A1A1A',
            border: `2px solid ${statusColor}`,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: `0 8px 24px ${statusColor}40`
            }
          }}
          onClick={() => setOpen(true)}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              position: 'relative'
            }}
          >
            <PeopleIcon
              sx={{
                fontSize: 28,
                color: '#fff'
              }}
            />
            {!loading && (
              <Fade in={!loading}>
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: statusColor,
                    backgroundColor: '#1A1A1A',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1px solid ${statusColor}`
                  }}
                >
                  {stats.total}
                </Typography>
              </Fade>
            )}
            <DotIcon
              sx={{
                fontSize: 12,
                color: statusColor,
                position: 'absolute',
                bottom: 8
              }}
            />
          </Box>
        </Paper>
      </Tooltip>

      <ActiveUsersOverlay open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default ActiveUsersWidget;

